import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Send, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { roleMatches, ROLES } from '../../config/hospitalRoles';

/**
 * Maker-checker panel for employee master approval workflow.
 */
const EmployeeMasterWorkflow = () => {
  const { user, hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');

  const canSubmit = roleMatches(user?.role_name, [ROLES.HR_MANAGER, ROLES.HR_EXECUTIVE, ROLES.PAYROLL_OPERATOR]);
  const canVerify = roleMatches(user?.role_name, [ROLES.FINANCE_MANAGER, ROLES.OPERATIONS_MANAGER, ROLES.FINANCE_APPROVER]);
  const canApprove = roleMatches(user?.role_name, [ROLES.HOSPITAL_DIRECTOR, ROLES.CHAIRMAN, ROLES.HOSPITAL_OWNER, ROLES.SUPER_ADMINISTRATOR]) || hasPermission('*');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/v1/employee-master/search', {
        params: { workflow_status: statusFilter, limit: 50 },
      });
      setItems(res.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const action = async (id, endpoint) => {
    await axios.post(`/v1/employee-master/${id}/${endpoint}`, { remarks: '', approve: true });
    load();
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Employee Approval Workflow</h3>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="draft">Draft</option>
            <option value="approved">Approved</option>
          </select>
          <button type="button" onClick={load} className="p-2 text-gray-600 hover:bg-gray-100 rounded">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      {loading ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500 text-sm">No employees in this workflow state.</p>
      ) : (
        <ul className="divide-y">
          {items.map((emp) => (
            <li key={emp.id} className="py-3 flex items-center justify-between">
              <div>
                <p className="font-medium">{emp.full_name} ({emp.emp_code})</p>
                <p className="text-sm text-gray-500">{emp.department_name} · {emp.workflow_status}</p>
              </div>
              <div className="flex gap-2">
                {canVerify && emp.workflow_status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => action(emp.id, 'verify')}
                    className="inline-flex items-center px-3 py-1 bg-amber-100 text-amber-800 rounded text-sm"
                  >
                    <Send className="w-3 h-3 mr-1" /> Verify
                  </button>
                )}
                {canApprove && ['pending', 'verified'].includes(emp.workflow_status) && (
                  <button
                    type="button"
                    onClick={() => action(emp.id, 'approve')}
                    className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded text-sm"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EmployeeMasterWorkflow;
